import * as React from 'react';
import { compose, setDisplayName, withHandlers, withState, withProps, lifecycle } from 'recompose';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { ReportSettings, ReportSettingsChangeHandler } from 'logic/reports/ReportSettings';
import { Fade } from 'ui/transitions';
import { makeSentenceFragments, renderFragment, FragmentRenderer } from 'util/makeSentenceFragments';
import { Input } from 'ui/shared/Input';

export type OwnProps = {
    reportSettings: Readonly<ReportSettings>;
    onChange: ReportSettingsChangeHandler;
};

const RawRecurrenceHourly = ({ enabled, sentenceFragments, renderFragment }: RecomposeProps & TranslateProps) =>
    <Fade
        id="edit-schedule-recurrence-panel-hourly"
        className="edit-schedule-recurrence-panel"
        visible={ enabled }
        duration={ 400 }
    >
        <div>
            { sentenceFragments.map(renderFragment) }
        </div>
    </Fade>
;

type RecomposeProps = {
    enabled: boolean;
    sentenceFragments: string[];
    renderFragment: FragmentRenderer;
    frequency: string;
    handleChangeFrequency: React.ChangeEventHandler<HTMLInputElement>;
};
type _RecomposeProps = RecomposeProps & {
    _setFrequency: (f: string) => void;
};

export const RecurrenceHourly = compose<RecomposeProps & TranslateProps, OwnProps>(
    setDisplayName('RecurrenceHourly'),
    translate(),
    withProps(({ reportSettings }: OwnProps) => ({
        enabled: reportSettings.scheduleType === 'Hourly',
    })),
    withState('frequency', '_setFrequency', ({ reportSettings }: OwnProps) =>
        reportSettings.scheduleType === 'Hourly'
            ? reportSettings.scheduleFrequency
            : 1,
    ),
    withHandlers({
        handleChangeFrequency: ({ onChange, _setFrequency }: OwnProps & _RecomposeProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const frequencyString = e.target.value.replace(/[^\d]/g, ''); // Strip non-digits
            _setFrequency(frequencyString);
            const frequency = parseInt(frequencyString, 10) || 1;
            onChange(reportSettings => reportSettings.setScheduleFrequency(frequency));
        },
    }),
    lifecycle({
        componentWillUpdate({ enabled, onChange, frequency }: OwnProps & _RecomposeProps) {
            // If this component becomes enabled, need to apply the current frequency value
            // to make sure the XML reflects the current values in the UI.
            if (!this.props.enabled && enabled) {
                onChange(reportSettings => reportSettings.setScheduleFrequency(parseInt(frequency, 10)));
            }
        },
    }),
    withProps(({ frequency, handleChangeFrequency, enabled, t }: RecomposeProps & TranslateProps) => ({
        sentenceFragments: makeSentenceFragments(t('WC_REPORT_SCHEDULE_HOURLY_SENTENCE', { count: parseInt(frequency, 10) })),
        renderFragment: renderFragment({
            INTERVAL: (
                <Input
                    type="number"
                    id="edit-report-hourly-hours"
                    value={ frequency }
                    onChange={ handleChangeFrequency }
                    min={ 1 }
                    max={ 23 }
                    maxLength={ 2 }
                    disabled={ !enabled }
                />
            ),
        }),
    })),
)(RawRecurrenceHourly);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/schedule/runOnSchedule/recurrence/RecurrenceHourly.tsx